import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import type {
  ActivityStatusCounts,
  EditGroupActivity,
  EditGroupMember,
  EditGroupSubgroup
} from '../../models/groups-management.model';

type ActivityStatusTab = 'incoming' | 'completed' | 'cancelled';

@Component({
  selector: 'bo-readonly-lists',
  standalone: true,
  imports: [TranslateModule, CommonModule],
  templateUrl: './readonly-lists.component.html',
  styleUrl: './readonly-lists.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReadonlyListsComponent {
  @Input() disabled = false;

  @Input() members: EditGroupMember[] = [];
  @Input() memberTotal = 0;
  @Input() membersExpanded = false;
  @Input() membersMoreCount = 0;
  @Input() canToggleMembers = false;

  @Input() subgroups: EditGroupSubgroup[] = [];
  @Input() subgroupTotal = 0;
  @Input() subgroupsExpanded = false;
  @Input() subgroupsMoreCount = 0;
  @Input() canToggleSubgroups = false;

  @Input() activities: EditGroupActivity[] = [];
  @Input() activityTotal = 0;
  @Input() activityStatusCounts: ActivityStatusCounts = {
    incoming: 0,
    completed: 0,
    cancelled: 0
  };
  @Input() activitiesExpanded = false;
  @Input() activitiesMoreCount = 0;
  @Input() canToggleActivities = false;

  @Input() getSportIconPath: (sport: string) => string = () => '';
  @Input() getActivityLevelLabel: (activity: EditGroupActivity) => string = () => 'All levels';
  @Input() getActivityTypeLabel: (activity: EditGroupActivity) => string = () => '';

  readonly activityStatusTabs: Array<{ label: string; value: ActivityStatusTab }> = [
    { label: 'statusLabels.incoming', value: 'incoming' },
    { label: 'statusLabels.completed', value: 'completed' },
    { label: 'statusLabels.cancelled', value: 'cancelled' }
  ];
  activeActivityStatus: ActivityStatusTab = 'incoming';

  @Output() toggleMembers = new EventEmitter<void>();
  @Output() toggleSubgroups = new EventEmitter<void>();
  @Output() toggleActivities = new EventEmitter<ActivityStatusTab>();
  @Output() activityStatusSelected = new EventEmitter<ActivityStatusTab>();
  @Output() subgroupOpened = new EventEmitter<EditGroupSubgroup>();
  @Output() activityOpened = new EventEmitter<EditGroupActivity>();
  @Output() thumbnailError = new EventEmitter<Event>();

  openSubgroup(subgroup: EditGroupSubgroup, event: Event) {
    event.stopPropagation();
    this.subgroupOpened.emit(subgroup);
  }

  openActivity(activity: EditGroupActivity, event: Event) {
    event.stopPropagation();
    this.activityOpened.emit(activity);
  }

  getMemberAvatarUrl(member: EditGroupMember): string | null {
    const avatarUrl = member.avatarUrl?.trim();
    return avatarUrl || null;
  }

  onMemberAvatarError(member: EditGroupMember) {
    member.avatarUrl = undefined;
  }

  hasThumbnailUrl(item: EditGroupSubgroup | EditGroupActivity): boolean {
    return !!item.thumbnailUrl?.trim();
  }

  onSubgroupThumbnailError(subgroup: EditGroupSubgroup) {
    subgroup.thumbnailUrl = '';
  }

  onActivityThumbnailError(activity: EditGroupActivity) {
    activity.thumbnailUrl = '';
  }

  setActiveActivityStatus(status: ActivityStatusTab) {
    if (this.activeActivityStatus === status) {
      this.activityStatusSelected.emit(status);
      return;
    }

    this.activeActivityStatus = status;
    this.activityStatusSelected.emit(status);
  }

  getActivityStatusCount(status: ActivityStatusTab): number {
    return this.activityStatusCounts[status] ?? 0;
  }

  getVisibleActivitiesByStatus(): EditGroupActivity[] {
    const filteredActivities = this.activities.filter(
      activity => this.toActivityStatusTab(activity.status) === this.activeActivityStatus
    );

    return this.activitiesExpanded ? filteredActivities : filteredActivities.slice(0, 2);
  }

  getActiveActivitiesMoreCount(): number {
    return this.activitiesExpanded ? 0 : Math.max(0, this.getActivityStatusCount(this.activeActivityStatus) - 2);
  }

  canToggleActiveActivities(): boolean {
    return this.activitiesExpanded || this.getActiveActivitiesMoreCount() > 0;
  }

  private toActivityStatusTab(status?: string): ActivityStatusTab {
    const normalizedStatus = status?.trim().toLowerCase();
    switch (normalizedStatus) {
      case 'scheduled':
        return 'incoming';
      case 'finished':
        return 'completed';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'incoming';
    }
  }
}
